import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/reveal";
import SEO from "@/components/seo";
import { ShieldCheck, Award, Heart, Users, Star } from "lucide-react";

export default function About() {
  return (
    <div className="flex flex-col w-full">
      <SEO
        title="About Us | Woman-Owned Pet Care in Tallahassee"
        description="Tallahassee Lassie is your local, woman-owned pet service. With over 20 years of experience, we are certified, fully bonded and insured with professional animal care training. Named Best Pet Business in Florida."
        path="/about"
      />
      <section className="relative bg-primary text-primary-foreground py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/16617bc9-4b44-4480-8f95-5470f189f37e/IMG_4166_jpg.jpg')] bg-cover bg-center opacity-[0.12] mix-blend-overlay"></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-3xl">
            <Reveal>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                About Tallahassee Lassie
              </h1>
            </Reveal>
            <Reveal delay={150}>
              <p className="text-xl md:text-2xl text-primary-foreground/90 font-serif italic">
                "Your peace of mind is always on our mind."
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-12 gap-12 lg:gap-20 items-start">
            <div className="md:col-span-7 space-y-6">
              <Reveal>
                <p className="text-lg text-muted-foreground leading-relaxed italic border-l-4 border-primary pl-6">
                  Thank you for reading more about us! We are happy to offer five-star pet sitting and dog walking in the Tallahassee area. Our business started with the mentality that "your peace of mind is always on our mind" and we aim to keep that as our mission. We are also proud to offer our employees insurance, bonding, professional training on animal behaviors and handling. Our clients love that we have a 100% reliability rate, and am in constant communication with hassle-free booking and services! We have been named Best of Florida, Best of NextDoor, and nominated for Best of Tallahassee in the customer service category. These are important to small business, but our client satisfaction is most important of all. Read on to learn more about our team and our Active Retiree Program, peruse reviews, and more.
                </p>
              </Reveal>

              <Reveal>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Tallahassee Lassie Pet Care, LLC is a locally owned and operated pet service offering loving care to pets in the Tallahassee area. We don't just take care of your pets, we truly care about them!
                </p>
              </Reveal>

              <Reveal>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  With over 20 years of experience in the Tallahassee area, we are CPR and First Aid certified, connected to local vets, licensed and insured. Every service experience is customized to you and your pets' needs, with each sitter on our team being matched to you based on your pets' needs. We are dedicated to providing trustworthy and competent care, just ask about our <strong className="text-foreground">Reliability Guarantee</strong>!
                </p>
              </Reveal>

              <Reveal>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  We offer many services such as dog walking, Pupventures, cat care, dog taxi (transport to the vet, groomer, daycare, boarding, and more), farm sitting, and medication administration. We also like to cuddle and play with your cats, dogs and other fur-babies. Check out our <Link href="/menu" className="text-primary underline hover:text-primary/80">Services page</Link> for a more detailed listing.
                </p>
              </Reveal>

              <Reveal>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Our service area is from North to Eastern Tallahassee, including Central/Midtown and Northeast Tallahassee, and have recently added Southeast Tallahassee and Southwood to our service areas. We do travel outside of this area depending on availability, for an extended area rate, such as Bradfordville, Havana, Lloyd, Wakulla, and Franklin Counties. Please inquire for availability.
                </p>
              </Reveal>
            </div>

            <div className="md:col-span-5 space-y-8">
              <Reveal direction="right">
                <img
                  src="https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/16617bc9-4b44-4480-8f95-5470f189f37e/IMG_4166_jpg.jpg"
                  alt="Tallahassee Lassie Pet Care team"
                  className="w-full shadow-xl"
                  style={{ borderRadius: "24px 0 24px 0" }}
                />
              </Reveal>

              <Reveal direction="right" delay={150}>
                <div className="bg-primary/5 p-8 border border-primary/10" style={{ borderRadius: "24px 0 24px 0" }}>
                  <h3 className="font-serif text-xl font-bold text-primary mb-4">Our Core Values: PETSIT</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-center gap-2"><Star size={16} className="text-secondary shrink-0" /> <strong>P</strong>recision</li>
                    <li className="flex items-center gap-2"><Star size={16} className="text-secondary shrink-0" /> <strong>E</strong>thics</li>
                    <li className="flex items-center gap-2"><Star size={16} className="text-secondary shrink-0" /> <strong>T</strong>rustworthiness</li>
                    <li className="flex items-center gap-2"><Star size={16} className="text-secondary shrink-0" /> <strong>S</strong>ervice</li>
                    <li className="flex items-center gap-2"><Star size={16} className="text-secondary shrink-0" /> <strong>I</strong>ntegrity</li>
                    <li className="flex items-center gap-2"><Star size={16} className="text-secondary shrink-0" /> <strong>T</strong>houghtfulness</li>
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-6">
          <Reveal>
            <h2 className="font-serif text-3xl font-bold text-center mb-4">Why Choose a Professional Pet Sitter?</h2>
            <p className="text-center text-muted-foreground max-w-2xl mx-auto mb-12 italic">
              Using a professional pet sitter is not only beneficial for your pets, but also to you as a pet parent. Not all pet sitters are created equal, and it is important to find the right person for the job.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-10 max-w-4xl mx-auto mb-16">
            <Reveal>
              <div className="bg-primary/5 p-8 border border-primary/10 h-full" style={{ borderRadius: "24px 0 24px 0" }}>
                <h3 className="font-serif text-xl font-bold text-primary mb-4">Benefits to You</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2"><span className="text-primary mt-1">•</span> No breed restrictions!</li>
                  <li className="flex items-start gap-2"><span className="text-primary mt-1">•</span> Knowledge that your pet is safe and loved</li>
                  <li className="flex items-start gap-2"><span className="text-primary mt-1">•</span> Confidence that the pet sitter can responsibly manage any grooming or veterinary needs</li>
                  <li className="flex items-start gap-2"><span className="text-primary mt-1">•</span> Direct communication with your pet's sitter, and our company owner</li>
                  <li className="flex items-start gap-2"><span className="text-primary mt-1">•</span> Pupdates! We send pictures and updates about your pet while they are in our care</li>
                  <li className="flex items-start gap-2"><span className="text-primary mt-1">•</span> Home is secured, mail is collected, trash and recycling are managed, without imposing on neighbors, friends, or family</li>
                </ul>
              </div>
            </Reveal>
            <Reveal delay={150}>
              <div className="bg-secondary/5 p-8 border border-secondary/10 h-full" style={{ borderRadius: "24px 0 24px 0" }}>
                <h3 className="font-serif text-xl font-bold text-secondary mb-4">Benefits to Your Pets</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2"><span className="text-secondary mt-1">•</span> Staying at home so they are familiar and comfortable with their environment</li>
                  <li className="flex items-start gap-2"><span className="text-secondary mt-1">•</span> Safety and Security</li>
                  <li className="flex items-start gap-2"><span className="text-secondary mt-1">•</span> Consistency in routine</li>
                  <li className="flex items-start gap-2"><span className="text-secondary mt-1">•</span> Personalized attention, love, and care</li>
                  <li className="flex items-start gap-2"><span className="text-secondary mt-1">•</span> Continuing Medications as needed</li>
                  <li className="flex items-start gap-2"><span className="text-secondary mt-1">•</span> Having an emergency plan for your pet, as well as natural disasters</li>
                  <li className="flex items-start gap-2"><span className="text-secondary mt-1">•</span> Removing the turmoil and trauma of moving your pet to a new space</li>
                  <li className="flex items-start gap-2"><span className="text-secondary mt-1">•</span> Limits exposure to unknown pets, and any illnesses they may carry</li>
                </ul>
              </div>
            </Reveal>
          </div>
          <Reveal>
            <p className="text-center text-muted-foreground max-w-2xl mx-auto mb-12">
              We are proud to be the only pet sitting and dog walking service in Tallahassee that is nationally and internationally certified, fully bonded and insured, with W-2 employees — not contractors. We match clients based on need — we have a cat specialist, lead dog walker, a sitter who works specifically for farm care, and more. You'll be given a primary sitter, secondary backup, and emergency back-up.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: ShieldCheck, label: "Insured & Bonded" },
              { icon: Award, label: "Nationally Certified" },
              { icon: Users, label: "Background Checked" },
              { icon: Heart, label: "Pet CPR Trained" },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="card-lift text-center p-6 bg-white shadow-sm border border-border/50" style={{ borderRadius: "24px 0 24px 0" }}>
                  <div className="w-14 h-14 bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4 rounded-full">
                    <item.icon size={28} />
                  </div>
                  <span className="font-medium text-foreground">{item.label}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 md:px-6">
          <Reveal>
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl font-bold mb-4">Awards & Recognition</h2>
            </div>
          </Reveal>
          <Reveal>
            <div className="flex flex-wrap justify-center gap-6 max-w-3xl mx-auto">
              {[
                "Best of Florida 2020–2025",
                "Best of NextDoor 2020–2022",
                "Pet Services Association Best 2024",
                "Nominated Best of Tallahassee (Customer Service)",
              ].map((award, i) => (
                <div key={i} className="bg-white px-6 py-3 shadow-sm border border-border/50 text-sm font-medium text-foreground" style={{ borderRadius: "16px 0 16px 0" }}>
                  <Award size={14} className="inline mr-2 text-secondary" />
                  {award}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4 md:px-6">
          <Reveal>
            <div className="text-center">
              <img
                src="https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/f655897d-ae58-4573-a9ca-c68c0f942b93/tempcertlist.jpg"
                alt="Tallahassee Lassie Pet Care certifications and credentials"
                className="max-w-2xl w-full mx-auto shadow-lg"
                style={{ borderRadius: "24px 0 24px 0" }}
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6">Ready to meet the team?</h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90 text-lg h-14 px-8">
                <Link href="/our-team">
                  Meet Our Team
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg h-14 px-8">
                <Link href="/contact-us">
                  Get in Touch
                </Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
